import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import T from '../T';
import { useSiteContent } from '../../hooks/useSiteContent';
import { useLocalizedText } from '../../hooks/useLocalizedText';

const FUNCTION_URL = 'https://us-central1-yliulaw-37283.cloudfunctions.net/sendContactEmail';

const ContactPage: React.FC = () => {
  const navyBlue = '#1a237e';
  const { content } = useSiteContent();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, subject, message }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send message');
      }
      setStatus('sent');
      setName(''); setEmail(''); setSubject(''); setMessage('');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err.message);
    }
  };

  const textColor = navyBlue;
  const mutedTextColor = '#334155';
  const labelColor = navyBlue;
  const cardBg = '#ffffff';
  const pageTitle = useLocalizedText(content.contact.pageTitle);
  const introText = useLocalizedText(content.contact.intro);
  const torontoOfficeTitle = useLocalizedText(content.contact.torontoOffice.title);
  const beijingOfficeTitle = useLocalizedText(content.contact.beijingOffice.title);

  return (
    <div style={{ backgroundColor: '#ffffff', minHeight: '80vh', padding: '60px 0' }}>
      <div className="container">
        {/* Page Title */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{ color: '#0f172a', fontSize: '2.2rem', marginBottom: '12px' }}>
            {pageTitle}
          </h1>
          <p style={{ color: mutedTextColor, fontSize: '1.05rem' }}>
            {introText}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '40px', alignItems: 'start' }}>

          {/* Left: Contact Info + Map */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

            {/* Info Card */}
            <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '30px', boxShadow: 'var(--shadow)', border: '1px solid #dbe4ff' }}>
              <h2 style={{ color: navyBlue, marginBottom: '24px', fontSize: '1.3rem' }}>
                {torontoOfficeTitle}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <MapPin size={20} color="var(--secondary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '2px', color: labelColor }}><T>Address:</T></div>
                    {content.contact.torontoOffice.addressLines.map((line) => (
                      <div key={line} style={{ color: textColor, whiteSpace: 'pre-wrap' }}>{line}</div>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <Phone size={20} color="var(--secondary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '2px', color: labelColor }}><T>Phone:</T></div>
                    <a href={`tel:${content.contact.torontoOffice.phone || ''}`} style={{ color: textColor, textDecoration: 'none' }}>{content.contact.torontoOffice.phone}</a>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '2px', color: labelColor }}><T>Fax:</T></div>
                    <a href={`tel:${content.contact.torontoOffice.fax || ''}`} style={{ color: textColor, textDecoration: 'none' }}>{content.contact.torontoOffice.fax}</a>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <Mail size={20} color="var(--secondary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '2px', color: labelColor }}><T>Email:</T></div>
                    <a href={`mailto:${content.contact.torontoOffice.email || ''}`} style={{ color: navyBlue, textDecoration: 'none' }}>{content.contact.torontoOffice.email}</a>
                  </div>
                </div>
              </div>

              <hr style={{ margin: '24px 0', borderColor: '#eee' }} />

              <h2 style={{ color: navyBlue, marginBottom: '16px', fontSize: '1.3rem' }}>
                {beijingOfficeTitle}
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <MapPin size={20} color="var(--secondary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '2px', color: labelColor }}><T>Address:</T></div>
                    {content.contact.beijingOffice.addressLines.map((line) => (
                      <div key={line} style={{ color: textColor, whiteSpace: 'pre-wrap' }}>{line}</div>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <Phone size={20} color="var(--secondary-color)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '2px', color: labelColor }}><T>Phone:</T></div>
                    <a href={`tel:${content.contact.beijingOffice.phone || ''}`} style={{ color: textColor, textDecoration: 'none' }}>{content.contact.beijingOffice.phone}</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div style={{ borderRadius: '12px', overflow: 'hidden', boxShadow: 'var(--shadow)', border: '1px solid #dbe4ff' }}>
              <iframe
                title="Y. Liu Law Firm Location"
                src="https://maps.google.com/maps?q=43.8618761,-79.309312&zoom=17&output=embed"
                width="100%"
                height="280"
                style={{ border: 0, display: 'block' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: Contact Form */}
          <div style={{ backgroundColor: cardBg, borderRadius: '12px', padding: '36px', boxShadow: 'var(--shadow)', border: '1px solid #dbe4ff' }}>
            <h2 style={{ color: navyBlue, marginBottom: '8px', fontSize: '1.4rem' }}>
              <T>Send Us a Message</T>
            </h2>
            <p style={{ color: mutedTextColor, marginBottom: '28px', fontSize: '0.95rem' }}>
              <T>Fill out the form below and we'll get back to you shortly.</T>
            </p>

            {status === 'sent' ? (
              <div style={{ textAlign: 'center', padding: '50px 20px' }}>
                <CheckCircle size={56} color="#2e7d32" strokeWidth={1.5} style={{ marginBottom: '16px' }} />
                <h3 style={{ color: '#2e7d32', marginBottom: '10px' }}><T>Message Sent!</T></h3>
                <p style={{ color: textColor }}><T>Thank you for reaching out. We'll respond within 1–2 business days.</T></p>
                <button
                  onClick={() => setStatus('idle')}
                  className="btn btn-primary"
                  style={{ marginTop: '24px' }}
                >
                  <T>Send Another Message</T>
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {status === 'error' && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', backgroundColor: '#fff5f5', color: '#c62828', padding: '12px 16px', borderRadius: '8px', border: '1px solid #ffc1c1', marginBottom: '20px', fontSize: '0.9rem' }}>
                    <AlertCircle size={18} />
                    {errorMsg || <T>Something went wrong. Please try again.</T>}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div>
                    <label htmlFor="contact-name" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: labelColor }}><T>Full Name</T></label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      value={name}
                      onChange={e => setName(e.target.value)}
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', backgroundColor: '#ffffff', color: navyBlue }}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: labelColor }}><T>Email Address</T></label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      style={{ width: '100%', padding: '11px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', backgroundColor: '#ffffff', color: navyBlue }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label htmlFor="contact-subject" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: labelColor }}><T>Subject</T></label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', backgroundColor: '#ffffff', color: navyBlue }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label htmlFor="contact-message" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '0.9rem', color: labelColor }}><T>Message</T></label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={6}
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Please describe your legal matter or question..."
                    style={{ width: '100%', padding: '11px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.95rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: '#ffffff', color: navyBlue }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="btn btn-primary"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '14px', fontSize: '1rem' }}
                >
                  <Send size={18} />
                  {status === 'sending' ? <T>Sending...</T> : <T>Send Message</T>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
